import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GridViewWebComponent } from '../../../grid/gridView';
import {
  WEB_COMPONENT_DOWNLOAD_KIT_BUTTON,
  WEB_COMPONENT_GRID_VIEW,
  WEB_COMPONENT_GZ_TITLE,
} from '../../../webcomponents.constants';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { Package } from '../types/packages-grid-package';
import { GridViewMetadata, SelectionStoreMode, ViewConfiguration, ViewType } from '../../../grid/gridView/types';
import { GridConfig } from './GridConfig';
import { Column, ColumnKeys, DefaultViewProps, PackageData, SelectableAction } from '../types';
import { DownloadKitButtonSelectorWebComponent } from '../../downloadKitButtonSelector';
import { OnClickPackage } from '../events/events';
import { translateFn } from '../../../utils/translator.helper';

export class PackagesGridView extends BaseWebComponentView {
  public dom: {
    $wrapper: HTMLElement;
    $grid: GridViewWebComponent<Column, ColumnKeys, Package>;
    $title: HTMLElement;
    $hostElement: HTMLElement;

    createPackage: {
      $createPackageButton: GzButtonWebComponent;
      $createPackageButtonText: HTMLElement;
    };

    $downloadButton: DownloadKitButtonSelectorWebComponent;

    deletePackage: {
      $deletePackageButton: GzButtonWebComponent;
      $deletePackageButtonText: HTMLElement;
    };

    sendDownloadLinks: {
      $sendDownloadLinksButton: GzButtonWebComponent;
      $sendDownloadLinksButtonText: HTMLElement;
    };
  };

  public getDomElements(): void {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),
      $grid: this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW) as GridViewWebComponent<Column, ColumnKeys>,
      $title: this.shadow.querySelector(WEB_COMPONENT_GZ_TITLE),
      $hostElement: this.shadow,

      createPackage: {
        $createPackageButton: this.shadow.querySelector('#create-package-button'),
        $createPackageButtonText: this.shadow.querySelector('#create-package-button-text'),
      },

      $downloadButton: this.shadow.querySelector(WEB_COMPONENT_DOWNLOAD_KIT_BUTTON),

      deletePackage: {
        $deletePackageButton: this.shadow.querySelector('#delete-package-button'),
        $deletePackageButtonText: this.shadow.querySelector('#delete-package-button-text'),
      },

      sendDownloadLinks: {
        $sendDownloadLinksButton: this.shadow.querySelector('#send-download-links-button'),
        $sendDownloadLinksButtonText: this.shadow.querySelector('#send-download-links-button-text'),
      },
    };

    this.getTranslations();
    this.setTranslatedStrings();
  }

  public setTranslatedStrings(): void {
    this.dom.$title.textContent = translateFn('Installation packages');
    this.dom.createPackage.$createPackageButtonText.innerText = translateFn('Create');
    this.dom.deletePackage.$deletePackageButtonText.innerText = translateFn('Delete');
    this.dom.sendDownloadLinks.$sendDownloadLinksButtonText.innerText =
      this.dependencies.translateFn('Send download links');
  }

  public enableDisableDownloadBtn(enabled: boolean): void {
    this.dom.$downloadButton.enableDownloadButton(enabled);
  }

  public enableDisableSendDownload(enabled: boolean): void {
    this.enableDisableActionButtons(enabled, SelectableAction.DOWNLOAD_MORE_ACTIONS);
  }

  public enableDisableDelete(enabled: boolean): void {
    this.enableDisableActionButtons(enabled, SelectableAction.DELETE_MORE_ACTIONS);
  }

  private enableDisableActionButtons(enabled: boolean, selectableAction: SelectableAction): void {
    switch (selectableAction) {
      case SelectableAction.DELETE_MORE_ACTIONS:
        this.dom.deletePackage.$deletePackageButton.disabled = !enabled;
        break;
      case SelectableAction.DOWNLOAD_MORE_ACTIONS:
        this.dom.sendDownloadLinks.$sendDownloadLinksButton.disabled = !enabled;
        break;
      default:
        break;
    }
  }

  public buildSuccessView(): void {
    const dispatcher = {
      packageNameClickEvent: this.dispatchPackageNameClickedEvent.bind(this),
    };

    const { viewMetaData, defaultViewProps } = new GridConfig(dispatcher).getDataForView(
      this.dependencies,
      this.stringsTranslator
    );

    this.buildGrid(viewMetaData, defaultViewProps);
    if (!this.dom.$downloadButton.started) {
      this.dom.$downloadButton.injectDependencies(this.dependencies);
      this.dom.$downloadButton.start().then().catch(console.error);
      this.dom.$downloadButton.enableDownloadButton(false);
    }
  }

  dispatchPackageNameClickedEvent(packageData: PackageData) {
    this.dom.$grid.dispatchEvent(new OnClickPackage(packageData));
  }

  private buildGrid(viewMetaData: GridViewMetadata<Column, ColumnKeys>, defaultViewData: DefaultViewProps): void {
    const grid = this.dom.$grid;

    const defaultViewProps: ViewConfiguration<Column, ColumnKeys> = {
      compactView: defaultViewData.compactView,
      displayedColumns: defaultViewData.displayedColumns,
      filters: defaultViewData.filters,
      name: '',
      pageSize: defaultViewData.pageSize,
      type: ViewType.DEFAULT,
      id: viewMetaData.viewId,
    };

    grid.propertiesSet = false;
    grid.sideMenuParent = this.dom.$hostElement;
    this.dom.$grid.selectionStoreConfig = {
      functionalMode: SelectionStoreMode.RECORDS,
    };

    this.dom.$grid.customStrings = {
      noSearchResultsString: this.dependencies.translateFn('No packages'),
    };
    for (const [key, value] of Object.entries(viewMetaData)) {
      grid[key] = value;
    }
    grid.defaultViewProps = defaultViewProps;
    grid.propertiesSet = true;
    grid.injectDependencies(this.dependencies);
    grid.start().catch();
  }
}
